<template>
  <div
    class="feature-card"
    :class="{
      'feature-card--reverse': reverse,
      [`feature-card--${theme}`]: theme,
    }"
  >
    <template v-if="hasVideo">
      <figure
        v-intersect="{
          handler: handleVideoIntersect,
          options: {
            threshold: 0.3,
            rootMargin: '50px',
          },
        }"
        class="m-0"
        @click="handleVideoClick"
      >
        <video
          v-if="shouldLoad"
          ref="videoRef"
          class="feature-card__image"
          :poster="image"
          muted
          playsinline
          webkit-playsinline
          x5-video-player-type="h5"
          x5-video-player-fullscreen="false"
          x5-playsinline
          x-webkit-airplay="allow"
          loop
          preload="auto"
          disablePictureInPicture
          controlsList="nodownload nofullscreen noremoteplayback"
        >
          <source
            v-for="(source, index) in videoSources"
            :key="index"
            :src="source.src"
            :type="source.type"
          />
        </video>
        <img v-else class="feature-card__image" :src="image" :alt="alt || 'alt'" loading="lazy" />
        <figcaption class="feature-card__caption">{{ videoDesc }}</figcaption>
      </figure>
    </template>
    <template v-else>
      <img
        v-intersect="{
          handler: (isIntersecting: boolean) => {
            shouldLoad = isIntersecting;
          },
          options: {
            threshold: 0,
            rootMargin: '200px',
          },
        }"
        class="feature-card__image"
        :src="shouldLoad ? image : undefined"
        :data-src="image"
        :alt="alt || 'alt'"
        loading="lazy"
      />
    </template>
    <div class="feature-card__intro">
      <div class="feature-card__text">
        <h3 class="feature-card__title">{{ title }}</h3>
        <p class="feature-card__description">{{ description }}</p>
      </div>
      <button class="feature-card__button" @click="handleCreateWork">
        {{ btnText }}
        <ArrowRight class="feature-card__icon" filled />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useBreakpoint } from '@rendora/design/index';
import ArrowRight from '~/assets/svg/icon-arrow-right.svg?component';

const headers = useRequestHeaders();
const { isMobile } = useBreakpoint(headers?.['user-agent']);
const videoAutoplay = useGlobalVideoAutoplay();

const props = withDefaults(
  defineProps<{
    image?: string;
    alt?: string;
    title: string;
    description: string;
    linkUrl?: string;
    reverse?: boolean;
    theme?: 'light' | 'dark';
    video?: string;
    sources?: Array<{ src: string; type?: string }>; // 多个视频源支持
    videoDesc?: string;
    btnText?: string;
    action?: () => void;
  }>(),
  { btnText: 'Get Started for Free' },
);

const videoRef = ref<HTMLVideoElement | null>(null);
const isInViewport = ref(false);
const shouldLoad = ref(false); // 控制资源懒加载
const hasVideo = computed(() => Boolean(props.video || props.sources?.length));

// 生成 MP4 fallback URL
const getMp4FallbackUrl = (url: string): string => {
  const lastDotIndex = url.lastIndexOf('.');
  if (lastDotIndex === -1) {
    return `${url}.mp4`;
  }
  return `${url.substring(0, lastDotIndex)}.mp4`;
};

// 构建视频源列表
const videoSources = computed(() => {
  // 在 SSR 时只使用 isMobile，避免访问 window 对象导致构建错误
  // isMobile 应该已经提供了正确的值，不需要额外检查 ontouchstart
  const mobile = isMobile.value;
  let sourcesToUse: Array<{ src: string; type?: string }> = [];

  // 如果有 sources 配置，使用它
  if (props.sources && props.sources.length > 0) {
    sourcesToUse = [...props.sources];
  } else if (props.video) {
    // 如果只有单个 video，添加 mp4 fallback
    const ext = props.video.split('.').pop()?.toLowerCase();
    if (ext) {
      const typeMap: Record<string, string> = {
        mp4: 'video/mp4',
        webm: 'video/webm',
        ogg: 'video/ogg',
      };

      const originalSource = {
        src: props.video,
        type: typeMap[ext] || `video/${ext}`,
      };

      // 如果不是 mp4，添加 mp4 fallback
      if (ext !== 'mp4') {
        const mp4Fallback = {
          src: getMp4FallbackUrl(props.video),
          type: 'video/mp4',
        };

        // 移动端：mp4 放在前面，优先使用
        if (mobile) {
          sourcesToUse.push(mp4Fallback);
          sourcesToUse.push(originalSource);
        } else {
          // PC端：原始格式放前面
          sourcesToUse.push(originalSource);
          sourcesToUse.push(mp4Fallback);
        }
      } else {
        sourcesToUse.push(originalSource);
      }
    } else {
      sourcesToUse.push({ src: props.video });
    }
  }

  // 如果是移动端且有多个sources，确保mp4优先
  if (mobile && sourcesToUse.length > 1) {
    // 将mp4格式的source移到前面
    const mp4Sources = sourcesToUse.filter(s => s.type?.includes('mp4'));
    const otherSources = sourcesToUse.filter(s => !s.type?.includes('mp4'));
    return [...mp4Sources, ...otherSources];
  }

  return sourcesToUse;
});

// 尝试播放视频
const tryPlayVideo = async () => {
  if (!videoRef.value || !isInViewport.value) return;

  // 如果需要解锁且还未解锁，等待用户交互
  if (videoAutoplay.needsUnlock.value && !videoAutoplay.isUnlocked.value) {
    // console.debug('Waiting for video unlock...');
    return;
  }

  try {
    const video = videoRef.value;

    // 确保视频已加载
    if (video.readyState < 2) {
      video.load();
      // 等待加载完成
      await new Promise(resolve => {
        video.addEventListener('canplay', resolve, { once: true });
        setTimeout(resolve, 2000); // 2秒超时
      });
    }

    // 尝试播放
    await video.play();
    // console.debug('Video playing');
  } catch {
    // console.debug('Video play failed:', error.message);
  }
};

// 暂停视频
const pauseVideo = () => {
  if (!videoRef.value) return;
  try {
    if (!videoRef.value.paused) {
      videoRef.value.pause();
    }
  } catch {
    // console.debug('Video pause failed:', error);
  }
};

// 处理视频点击 - 用户手动播放
const handleVideoClick = (e: MouseEvent) => {
  e.stopPropagation();
  if (videoRef.value && videoRef.value.paused) {
    tryPlayVideo();
  }
};

// 处理视频进入/离开视口
const handleVideoIntersect = (isIntersecting: boolean) => {
  isInViewport.value = isIntersecting;

  if (isIntersecting) {
    shouldLoad.value = true; // 进入视口时开始加载资源
    nextTick(() => {
      tryPlayVideo();
    });
  } else {
    pauseVideo();
    shouldLoad.value = false;
  }
};

// 监听解锁状态变化
watch(
  () => videoAutoplay.isUnlocked.value,
  unlocked => {
    if (unlocked && isInViewport.value && videoRef.value) {
      // console.debug('Video unlocked, trying to play...');
      tryPlayVideo();
    }
  },
);

watch(videoRef, (newVideo, oldVideo) => {
  if (oldVideo) {
    videoAutoplay.unregisterVideo(oldVideo);
  }

  if (newVideo) {
    videoAutoplay.registerVideo(newVideo);
  }
});

onMounted(() => {
  // 设置全局解锁监听器（只在第一次组件挂载时执行一次）
  videoAutoplay.setupUnlockListeners();

  // 如果不使用视频，图片的懒加载需要单独处理
  // 视频的懒加载由 handleVideoIntersect 处理
});

onBeforeUnmount(() => {
  // 注销视频元素
  if (videoRef.value) {
    videoAutoplay.unregisterVideo(videoRef.value);
  }
  pauseVideo();
});

const handleCreateWork = () => {
  if (props.action) {
    props.action();
    return;
  }
  const userInfo = useUserInfo();
  if (!userInfo.value) {
    navigateTo(
      `/login?redirect=${encodeURIComponent(encodeURIComponent(`${location.origin}/workspace`))}`,
    );
  } else {
    location.href = '/workspace';
  }
};
</script>

<style lang="scss" scoped src="./pc.scss" />
<style lang="scss" scoped src="./mobile.scss" />
